import { applyJourneyBatch3Explanation } from './journeyBatch3Explanations.js';
import { applyJourneyBatch4Explanation } from './journeyBatch4Explanations.js';
import { applyJourneyBatch5Explanation } from './journeyBatch5Explanations.js';
import { applyJourneyBatch6Explanation } from './journeyBatch6Explanations.js';
import { applyJourneyBatch7Explanation } from './journeyBatch7Explanations.js';
import { applyJourneyTerminologyCoverage } from './journeyTerminologyCoverage.js';

// Gold-standard stage-level What You Might Hear selections for Specialty and
// Major Pathway Journeys. Keep these deliberately selective and only reference
// phrases that exist in the shared catalogue, so raw i18n keys cannot surface.
const SPECIALTY_STAGE_PHRASES = Object.freeze({
  cardiology: Object.freeze([
    Object.freeze(['referral', 'observations', 'ecg', 'bloodTests', 'troponinBloodTest', 'echocardiogram', 'heartRhythmMonitoring']),
    Object.freeze(['monitoring', 'cannula', 'angiogram', 'angioplasty', 'stent', 'review', 'carePlan']),
    Object.freeze(['takeHomeMedicines', 'cardiacRehabilitation', 'safetyNetting', 'followUp']),
  ]),
  stroke: Object.freeze([
    Object.freeze(['observations', 'news2', 'ctScan', 'monitoring', 'review']),
    Object.freeze(['mdt', 'physiotherapy', 'occupationalTherapy']),
    Object.freeze(['medicallyFitForDischarge', 'carePlan', 'safetyNetting', 'followUp']),
  ]),
  orthopaedics: Object.freeze([
    Object.freeze(['referral', 'scan', 'preAssessment', 'consent']),
    Object.freeze(['theatre', 'recovery', 'analgesia']),
    Object.freeze(['physiotherapy', 'vtePrevention', 'takeHomeMedicines', 'safetyNetting', 'followUp']),
  ]),
  'neurology-neurosurgery': Object.freeze([
    Object.freeze(['referral', 'mriScan', 'ctScan', 'review']),
    Object.freeze(['consent', 'theatre', 'monitoring']),
    Object.freeze(['physiotherapy', 'occupationalTherapy', 'safetyNetting', 'followUp']),
  ]),
  paediatrics: Object.freeze([
    Object.freeze(['reasonableAdjustments', 'nilByMouth', 'playSpecialist', 'consent']),
    Object.freeze(['observations', 'cannula', 'playSpecialist', 'bloodTests', 'scan', 'consent', 'monitoring', 'review', 'carePlan']),
    Object.freeze(['takeHomeMedicines', 'safetyNetting', 'followUp']),
  ]),
  renal: Object.freeze([
    Object.freeze(['kidneyFunctionTests', 'bloodTests', 'fluidBalance', 'aki', 'observations']),
    Object.freeze(['dialysis', 'fluidBalance', 'monitoring', 'carePlan']),
    Object.freeze(['kidneyFunctionTests', 'takeHomeMedicines', 'followUp', 'safetyNetting']),
  ]),
  respiratory: Object.freeze([
    Object.freeze(['observations', 'oxygenSaturations', 'bloodTests', 'spirometry', 'chestXRay', 'ctScan', 'bloodGasTest']),
    Object.freeze(['oxygenTherapy', 'nebuliser', 'cpap', 'monitoring', 'review', 'respiratoryPhysiotherapy']),
    Object.freeze(['pulmonaryRehabilitation', 'safetyNetting', 'followUp']),
  ]),
  'mental-health': Object.freeze([
    Object.freeze(['mentalHealthAssessment', 'riskAssessment', 'confidentiality']),
    Object.freeze(['carePlan', 'peerSupport', 'confidentiality', 'mdt', 'review']),
    Object.freeze(['careCoordinator', 'crisisPlan', 'followUp', 'referral']),
  ]),
  'surgery-operation': Object.freeze([
    Object.freeze(['preAssessment', 'nilByMouth', 'consent']),
    Object.freeze(['cannula', 'theatre', 'analgesia', 'recovery']),
    Object.freeze(['observations', 'mobilise', 'fluidBalance', 'medicallyFitForDischarge', 'takeHomeMedicines', 'safetyNetting']),
  ]),
  'inpatient-admission': Object.freeze([
    Object.freeze(['admission', 'observations', 'news2']),
    Object.freeze(['wardRound', 'handover', 'review', 'carePlan', 'prn', 'iv', 'fluidBalance', 'mobilise', 'mdt']),
    Object.freeze(['medicallyFitForDischarge', 'dischargePlanning', 'takeHomeMedicines', 'dischargeSummary', 'safetyNetting']),
  ]),
  'discharge-recovery': Object.freeze([
    Object.freeze(['medicallyFitForDischarge', 'dischargePlanning', 'takeHomeMedicines', 'dischargeSummary']),
    Object.freeze(['analgesia', 'mobilise', 'safetyNetting']),
    Object.freeze(['followUp', 'followUpAppointment', 'safetyNetting']),
  ]),
  'cancer-care': Object.freeze([
    Object.freeze(['oncology', 'clinicalNurseSpecialist', 'cancerMdt', 'bloodTests', 'scan', 'biopsy']),
    Object.freeze(['chemotherapy', 'radiotherapy', 'immunotherapy', 'treatmentCycle', 'piccLine', 'port', 'sideEffects']),
    Object.freeze(['neutropenia', 'supportiveCare', 'followUpScan', 'safetyNetting', 'followUp']),
  ]),
});

export function applySpecialtyJourneyStageQuality(templateId, stages = []) {
  const phraseStages = SPECIALTY_STAGE_PHRASES[templateId];
  if (!phraseStages || phraseStages.length !== stages.length) return stages;
  const enrichedStages = stages.map((stage, index) => ({ ...stage, phraseKeys: [...phraseStages[index]] }));
  return applyJourneyTerminologyCoverage(templateId, applyJourneyBatch7Explanation(templateId, applyJourneyBatch6Explanation(templateId, applyJourneyBatch5Explanation(templateId, applyJourneyBatch4Explanation(templateId, applyJourneyBatch3Explanation(templateId, enrichedStages))))));
}
